import React, { useContext, useEffect, useState } from 'react';
import { EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../components/Dropdown';
import { Badge } from '../../components/Badge';
import { Link } from '../../components/Link';
import { Button } from '../../components/Button';
import { Heading, Subheading } from '../../components/Heading';
import { Input } from '../../components/Input';
import { Divider } from '../../components/Divider';
import { Avatar } from '../../components/Avatar';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../components/Dialog';
import { useWarehouseProduct } from '../../hooks/useWarehouseProduct';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../context/userContext';
import { WarehouseProduct } from '../../models/WarehouseProduct';
import { Text } from '../../components/Text';
import { Card } from '../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../components/FiltersDialog';
import { DateRangePicker } from '../../components/DateRangePicker';
import { Select } from '../../components/Select';
import { useWarehouses } from '../../hooks/useWarehouse';
import { useWarehouseProductItemBatchNo } from '../../hooks/useWarehouseProductItemBatchNo';

const COLUMNS = [
    { key: 'description', label: 'Description' },
    { key: 'itemNo', label: 'Item No' },
    { key: 'quantity', label: 'Current Quantity' },
    { key: 'created', label: 'Created on' },
    { key: 'updated', label: 'Last Updated on' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const StockBalanceTransferPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const {
        loading,
        error,
        deleteProduct,
        products,
        fetchAllFilteredProductsOffset,
        totalProducts,
    } = useWarehouseProduct();
    const { warehouses } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10); // Fixed at 10 items per page
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState({
        startDate: '',
        endDate: '',
        year: '',
        warehouseId: ''
    });

    // Initial load
    useEffect(() => {
        document.title = "Stock Balance Transfer";
        fetchProductsPaginated();
    }, []);

    // Fetch products when page or filters change
    useEffect(() => {
        fetchProductsPaginated();
    }, [currentPage, activeSearchQuery, activeFilters]);

    const fetchProductsPaginated = () => {
        const warehouseId = (currentUser?.warehouse_hq === "1") ? activeFilters.warehouseId : currentUser?.warehouse_id?.toString();

        fetchAllFilteredProductsOffset(
            limit,
            (currentPage - 1) * limit,
            activeSearchQuery,
            activeFilters.startDate,
            activeFilters.endDate,
            activeFilters.year,
            warehouseId || ''
        );
    };

    const handleSearch = () => {
        setCurrentPage(1);
        setActiveSearchQuery(searchQuery);
        setActiveFilters({
            startDate: filterStartDate,
            endDate: filterEndDate,
            year: filterYear,
            warehouseId: filterWarehouseId
        });
    };

    const handleReset = () => {
        setSearchQuery('');
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
        setActiveSearchQuery('');
        setActiveFilters({
            startDate: '',
            endDate: '',
            year: '',
            warehouseId: ''
        });
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const availableYears = Array.from(
        new Set(
            products
                .map((p) => new Date(p.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [activeFilters.startDate, activeFilters.endDate, activeFilters.year, activeFilters.warehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(activeFilters.startDate ? [{ key: 'from', label: `From: ${activeFilters.startDate}`, onClear: () => { setFilterStartDate(''); setActiveFilters((prev) => ({ ...prev, startDate: '' })); } }] : []),
        ...(activeFilters.endDate ? [{ key: 'to', label: `To: ${activeFilters.endDate}`, onClear: () => { setFilterEndDate(''); setActiveFilters((prev) => ({ ...prev, endDate: '' })); } }] : []),
        ...(activeFilters.year ? [{ key: 'year', label: `Year: ${activeFilters.year}`, onClear: () => { setFilterYear(''); setActiveFilters((prev) => ({ ...prev, year: '' })); } }] : []),
        ...(activeFilters.warehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === activeFilters.warehouseId)?.warehouse_name || activeFilters.warehouseId}`,
            onClear: () => { setFilterWarehouseId(''); setActiveFilters((prev) => ({ ...prev, warehouseId: '' })); },
        }] : []),
    ];

    const clearAllFilterPills = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
        setActiveFilters({ startDate: '', endDate: '', year: '', warehouseId: '' });
    };

    // Filter products based on active search/filters
    const filteredProducts = products.filter((product) => {
        const matchesSearch = activeSearchQuery
            ? product.description?.toLowerCase().includes(activeSearchQuery.toLowerCase())
            : true;
        const matchesStartDate = !activeFilters.startDate || new Date(product.created_time) >= new Date(activeFilters.startDate);
        const matchesEndDate = !activeFilters.endDate || new Date(product.created_time) <= new Date(activeFilters.endDate);
        const matchesYear = !activeFilters.year || new Date(product.created_time).getFullYear().toString() === activeFilters.year;
        const matchesWarehouse = !activeFilters.warehouseId || product.warehouse_id === Number(activeFilters.warehouseId);

        return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
    });

    const handleOpenDeleteDialog = (productId: string) => {
        setSelectedProductId(productId);
        setIsOpen(true);
    };

    const { createBatch } = useWarehouseProductItemBatchNo();
    const [editForm, setEditForm] = useState({
        productId: '',
        quantity: '',
        batch_no: '',
        expiry_date: ''
    });

    const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOpenEditDialog = (productId: string) => {
        setSelectedProductId(productId);
        setEditForm({
            productId,
            quantity: '',
            batch_no: '',
            expiry_date: ''
        });
        setIsOpen(true);
    };

    const handleEditSubmit = async () => {
        if (!selectedProductId) return;

        try {
            // Create the batch record
            await createBatch({
                product_id: Number(selectedProductId),
                batch_no: editForm.batch_no,
                expiry_date: editForm.expiry_date,
                supplier_id: null, // Explicitly set to null
                current_quantity: editForm.quantity,
                warehouse_id: Number(currentUser?.warehouse_id!) // Use current user's warehouse if available
            });

            // Here you would also update the product quantity
            // You'll need to implement this part based on your API
            // await updateProductQuantity(selectedProductId, editForm.quantity);

            setIsOpen(false);
            // Refresh the product list
            fetchProductsPaginated();
        } catch (error) {
            console.error('Failed to update product:', error);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>All Products</Heading>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={
                <ListToolbarSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    placeholder="Search product…"
                />
            }>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={clearAllFilterPills} />

            {/* Product Table */}
            <div className="mt-6">
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'description') && <TableHeader>Description</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'itemNo') && <TableHeader>Item No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'quantity') && <TableHeader>Current Quantity</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'updated') && <TableHeader>Last Updated on</TableHeader>}
                                    <TableHeader className="">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredProducts.length > 0) ? [...filteredProducts].sort((a, b) => {
                                    const diff = new Date(a.created_time || 0).getTime() - new Date(b.created_time || 0).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((product, index) => (
                                    <TableRow key={product.id} title={`${product.barcode}`}>
                                        <TableCell>{(currentPage - 1) * limit + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'description') && <TableCell className="text-zinc-500">{product.description ?? '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'itemNo') && <TableCell className="text-zinc-500">{product.item_no ?? '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'quantity') && <TableCell className="text-zinc-500">{product.current_quantity ?? '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(product.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'updated') && <TableCell className="text-zinc-500">{dateFormat(product.updated_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className=" text-zinc-500">
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5'>
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => navigate(`/gp/stockbalancetransfer/${product.id}`)}>
                                                        Update Quantity
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No product found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>

                        {/* Pagination */}
                        {totalProducts > 0 && (
                            <div className="mt-6">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={Math.ceil(totalProducts / limit)}
                                    onPageChange={handlePageChange}
                                    totalResult={totalProducts}
                                    dataName="Products"
                                    pageSize={limit}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
            </Card>

            <FiltersDialog
                open={isFilterOpen}
                onClose={() => { handleSearch(); setIsFilterOpen(false); }}
                onReset={handleReset}
            >
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year (Inserted Year)</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.filter((warehouse) => warehouse.hq !== '1').map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Update Product Quantity</DialogTitle>
                <DialogDescription>
                    Update the quantity and batch information for this product.
                </DialogDescription>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 mt-4">
                    <div className="sm:col-span-full">
                        <section>
                            <div className="space-y-1">
                                <Subheading>Quantity</Subheading>
                            </div>
                            <div className="mt-2">
                                <Input
                                    aria-label="Quantity"
                                    type="number"
                                    name="quantity"
                                    value={editForm.quantity}
                                    onChange={handleEditFormChange}
                                />
                            </div>
                        </section>
                    </div>
                    <div className="sm:col-span-full">
                        <section>
                            <div className="space-y-1">
                                <Subheading>Batch Number</Subheading>
                            </div>
                            <div className="mt-2">
                                <Input
                                    aria-label="Batch No"
                                    type="text"
                                    name="batch_no"
                                    value={editForm.batch_no}
                                    onChange={handleEditFormChange}
                                />
                            </div>
                        </section>
                    </div>
                    <div className="sm:col-span-full">
                        <section>
                            <div className="space-y-1">
                                <Subheading>Expiry Date</Subheading>
                            </div>
                            <div className="mt-2">
                                <Input
                                    aria-label="Expiry Date"
                                    type="date"
                                    name="expiry_date"
                                    value={editForm.expiry_date}
                                    onChange={handleEditFormChange}
                                />
                            </div>
                        </section>
                    </div>
                </div>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleEditSubmit}>Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default StockBalanceTransferPage;
